import React, { useCallback, useEffect } from 'react';

import { JRCTabs, JRCTabPanel, ItemEntriesProps } from 'components/JRCTabs/JRCTabs';
import { useIntl, FormattedMessage, IntlShape } from 'react-intl';

import { JRCFolders } from 'components/JRCFolders/JRCFolders';
import { arrayUniqueByUri } from 'utils/utils.array';

import styled from 'styled-components';

import { AudienceAutocomplete, jFolderList, JGroupApi, JUserApi } from 'jamespot-user-api';
import { JRCModal } from '../../../JRCModal/templates/JRCModal';
import { JRCUsers } from '../../../JRCUsers/JRCUsers';
import { JRCGroups } from '../../../JRCGroups/JRCGroups';
import { JRCBusinessContacts } from '../../../JRCBusinessContacts/JRCBusinessContacts';
import { JRCLoader } from '../../../JRCLoader/JRCLoader';
import { FoldersHandlers } from '../../../JRCFolders/types';

export const CSSViewsWrapper = styled.div`
    max-height: 700px;
    overflow: auto;
    padding: 0 ${({ theme }) => theme.space.sm}px;
`;

type ViewDefinition = {
    [K in AutocompleteView]?: Array<{
        title: string;
        key?: string;
    }>;
};

const GROUPS = 'groups';
const PUBLIC_GROUPS = 'public_groups';
const tabEntries = (views: Array<AutocompleteView>, intl: IntlShape): ItemEntriesProps[] => {
    const viewDefinitions: ViewDefinition = {
        folder: [{ title: intl.formatMessage({ id: 'GLOBAL_File_Bank' }) }],
        user: [{ title: intl.formatMessage({ id: 'GLOBAL_Users' }) }],
        group: [
            { key: GROUPS, title: intl.formatMessage({ id: 'GLOBAL_Groups' }) },
            { key: PUBLIC_GROUPS, title: intl.formatMessage({ id: 'GLOBAL_Public_Groups' }) },
        ],
        businessContact: [{ title: intl.formatMessage({ id: 'CONTACT_Pro_Members' }) }],
    };

    const tabItems: ItemEntriesProps[] = [];

    views.forEach((viewName: AutocompleteView) => {
        const definitions = viewDefinitions[viewName];
        if (definitions) {
            definitions.forEach((definition) => {
                tabItems.push({
                    key: definition.key ?? String(viewName),
                    label: definition.title,
                });
            });
        }
    });

    return tabItems;
};

export type AutocompleteView = 'folder' | 'user' | 'group' | 'businessContact';

export type AVProps = {
    views: Array<AutocompleteView>;
    initialValues: AudienceAutocomplete[];
    onConfirm: (values: any) => void;
    open: boolean;
    closeHandler: () => void;
    handlers: FoldersHandlers & {
        user: Pick<JUserApi, 'userQueryList' | 'userBusinessContactQueryList'>;
        group: Pick<JGroupApi, 'list'>;
    };
    onError?: (message?: string) => void;
};

const foldersToAutocomplete = (arr: jFolderList[]): AudienceAutocomplete[] => {
    return arr.map((folder) => {
        return {
            id: String(folder.id),
            uri: folder.uri,
            shortUri: folder.uri,
            title: folder.title,
            name: folder.title,
            mainType: folder.mainType,
            type: folder.type,
            label: folder.title,
            cssClass: '',
            cssColor: '',
            class: '',
        };
    });
};

const JRCInputAutocompleteViews = ({
    views,
    initialValues,
    onConfirm,
    open,
    closeHandler,
    handlers,
    onError,
}: AVProps) => {
    const intl = useIntl();

    const [activeItem, setActiveItem] = React.useState<string>(views[0] || '');
    const [localValues, setLocalValues] = React.useState<AudienceAutocomplete[]>();

    useEffect(() => {
        if (views.length > 0) {
            setActiveItem(views[0] === 'group' ? GROUPS : views[0]);
        }
    }, [views]);

    useEffect(() => {
        setLocalValues(initialValues);
    }, [initialValues]);

    const onSelectItem = useCallback(
        (items: AudienceAutocomplete[], types: string[]) => {
            setLocalValues(
                arrayUniqueByUri(localValues ?? [], items).filter(
                    (el) => !types.includes(el.type) || items.some((i) => i.uri === el.uri),
                ),
            );
        },
        [localValues],
    );

    return (
        <JRCModal
            open={open}
            closeHandler={closeHandler}
            title={intl.formatMessage({ id: 'PUBLISH_To' })}
            buttons={[
                {
                    color: 'cancel',
                    clickHandler: closeHandler,
                    children: <FormattedMessage id="GLOBAL_Cancel" />,
                },
                {
                    color: 'primary',
                    clickHandler: () => onConfirm(localValues),
                    children: <FormattedMessage id="GLOBAL_Confirm" />,
                },
            ]}>
            {localValues ? (
                <>
                    <JRCTabs
                        direction="horizontal"
                        entries={tabEntries(views, intl)}
                        activeItem={activeItem}
                        callback={(item) => setActiveItem(item)}
                    />
                    {/**
                     TODO : Harmoniser la gestion des folders et des users ; passage par les uris
                     d'un côté et par les objets de l'autre.
                     **/}
                    <JRCTabPanel active={activeItem === 'folder'}>
                        <CSSViewsWrapper>
                            <JRCFolders
                                onError={onError}
                                handlers={handlers}
                                initialFolders={(localValues ?? [])
                                    .filter((el) => el.type === 'rootFolder' || el.type === 'folder')
                                    .map((el) => el.uri)}
                                onSelectFolder={(folders) =>
                                    onSelectItem(foldersToAutocomplete(folders), ['rootFolder', 'folder'])
                                }
                                searchable={true}
                            />
                        </CSSViewsWrapper>
                    </JRCTabPanel>
                    <JRCTabPanel active={activeItem === 'user'}>
                        <CSSViewsWrapper>
                            <JRCUsers
                                handlers={handlers}
                                onError={onError}
                                initialUsers={localValues.filter((el) => el.type === 'user')}
                                onSelectUser={(users) => onSelectItem(users, ['user'])}
                            />
                        </CSSViewsWrapper>
                    </JRCTabPanel>
                    <JRCTabPanel active={activeItem === GROUPS}>
                        <CSSViewsWrapper>
                            <JRCGroups
                                handlers={handlers}
                                onError={onError}
                                initialGroups={localValues.filter((el) => el.type === 'spot')}
                                onSelectGroup={(groups) => onSelectItem(groups, ['spot'])}
                            />
                        </CSSViewsWrapper>
                    </JRCTabPanel>
                    <JRCTabPanel active={activeItem === PUBLIC_GROUPS}>
                        <CSSViewsWrapper>
                            <JRCGroups
                                handlers={handlers}
                                onError={onError}
                                initialGroups={localValues.filter((el) => el.type === 'spot')}
                                onSelectGroup={(groups) => onSelectItem(groups, ['spot'])}
                                publicOnly={true}
                            />
                        </CSSViewsWrapper>
                    </JRCTabPanel>
                    <JRCTabPanel active={activeItem === 'businessContact'}>
                        <CSSViewsWrapper>
                            <JRCBusinessContacts
                                handlers={handlers}
                                onError={onError}
                                initialBusinessContacts={localValues.filter((el) => el.type === 'businessContactCard')}
                                onSelectBusinessContact={(businessContact) =>
                                    onSelectItem(businessContact, ['businessContactCard'])
                                }
                            />
                        </CSSViewsWrapper>
                    </JRCTabPanel>
                </>
            ) : (
                <JRCLoader size={'l'} />
            )}
        </JRCModal>
    );
};

export { JRCInputAutocompleteViews };
